import { configureStore } from '@reduxjs/toolkit'
import app, { auth } from '../lib/firebase'
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore'
import uiReducer from './slices/uiSlice'
import tabsReducer from './slices/tabsSlice'
import casesReducer from './slices/casesSlice'
import documentsReducer from './slices/documentsSlice'
import notesReducer from './slices/notesSlice'
import settingsReducer from './slices/settingsSlice'
import sidebarReducer from './slices/sidebarSlice'
import analysisReducer from './slices/analysisSlice'
import agentEventsReducer from './slices/agentEventsSlice'
// Removed unused slices: extraction, results, documentProcessing (old job-based system)
import workReducer from './slices/workSlice'
import uploadQueueReducer from './slices/uploadQueueSlice'

export const store = configureStore({
  reducer: {
    ui: uiReducer,
    tabs: tabsReducer,
    cases: casesReducer,
    documents: documentsReducer,
    notes: notesReducer,
    settings: settingsReducer,
    sidebar: sidebarReducer,
    work: workReducer,
    analysis: analysisReducer,
    agentEvents: agentEventsReducer,
    uploadQueue: uploadQueueReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for non-serializable values
        ignoredActions: [
          'ui/setExpandedItems',
          'notes/addNote',
          'ui/addResearchMessage'
        ],
        // Ignore these paths in the state
        ignoredPaths: [
          'ui.tabs.openTabs',
          'notes.entities',
          'ui.research.messages',
          'work.runtimeById'  // Don't persist volatile runtime data
        ],
      },
    }),
})

// Persist minimal UI state to Firestore with localStorage backup
// Keeps user context across refresh/sessions
try {
  const db = getFirestore(app)
  let persistTimer: number | undefined

  const persistUiState = async () => {
    try {
      const state = store.getState()
      const ui = state.ui
      const selectedCaseId = state.cases?.selectedCaseId ?? null

      const persisted = {
        activeTabId: state.tabs.activeTabId,
        openTabs: state.tabs.openTabs.map((t: any) => ({
          id: t.id,
          label: t.label,
          type: t.type,
          closable: !!t.closable,
          iconName: t.iconName ?? undefined,
          metadata: t.metadata ?? undefined,
        })),
        layout: ui.layout,
        bottomPanelTab: ui.layout.bottomPanelTab,
        selectedCaseId,
      }

      // Local backup
      if (typeof window !== 'undefined') {
        try { localStorage.setItem('uiState', JSON.stringify(persisted)) } catch {}
      }

      // Firestore (if authenticated)
      const user = auth.currentUser
      if (user) {
        await setDoc(
          doc(db, 'user_ui_state', user.uid),
          { state: persisted, updatedAt: serverTimestamp() },
          { merge: true }
        )
      }
    } catch {}
  }

  store.subscribe(() => {
    if (persistTimer) {
      window.clearTimeout(persistTimer)
    }
    // Debounce writes
    persistTimer = window.setTimeout(persistUiState, 500)
  })
} catch {}

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch